import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  format,
  isToday,
  isYesterday,
  startOfDay,
  isSameDay,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth
} from 'date-fns';
import jsPDF from 'jspdf';
import {
  FiArrowDown,
  FiMic,
  FiSend,
  FiMoreHorizontal,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiDownload,
  FiCheckSquare,
  FiSquare,
  FiClipboard,
  FiUser,
  FiAward,
  FiCpu,
  FiHash,
  FiEdit,
  FiTrash2,
  FiLogOut,
  FiSettings,
  FiX,
  FiBarChart2,
} from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import Auth from './components/Auth';
import UserSettings from './components/UserSettings';
import Analytics from './components/Analytics';
import { useAuth } from './hooks/useAuth';
import { useEntries } from './hooks/useEntries';
import supabase from './supabase/supabase';

const availableTags = [
  { key: 'personal', label: 'Personal', icon: FiUser },
  { key: 'hustle', label: 'Hustle', icon: FiAward },
];

const TEMPLATES = [
  {
    id: 'daily',
    name: 'Daily Reflection',
    icon: '📅',
    keywords: ['daily', 'reflection', 'day'],
    content: `What went well today?
{cursor}

What challenged me?


Grateful for:

`
  },
  {
    id: 'weekly',
    name: 'Weekly Review',
    icon: '📊',
    keywords: ['weekly', 'review', 'week'],
    content: `Week of {week}

Key wins:
{cursor}

What I learned:


Next week priorities:

`
  },
  {
    id: 'meeting',
    name: 'Meeting Notes',
    icon: '👥',
    keywords: ['meeting', 'notes', 'agenda'],
    content: `Meeting:
Date: {date}
People:

Agenda:
{cursor}

Notes:


Action items:
- [ ]
`
  },
  {
    id: 'morning',
    name: 'Morning Pages',
    icon: '✍️',
    keywords: ['morning', 'pages', 'thoughts'],
    content: `Morning thoughts - {day}, {date}

{cursor}`
  },
  {
    id: 'gratitude',
    name: 'Gratitude Journal',
    icon: '🙏',
    keywords: ['gratitude', 'grateful', 'thankful'],
    content: `Three things I'm grateful for today:

1. {cursor}
2.
3.

Why:

`
  }
];

function App() {
  const { user, profile, loading: authLoading, signIn, signUp, signOut, updateProfile, updatePassword } = useAuth();
  const { entries, loading: entriesLoading, addEntry, updateEntry, deleteEntry, toggleTodoCompletion } = useEntries(user?.id);
  
  const [newEntry, setNewEntry] = useState('');
  const [activeTab, setActiveTab] = useState('timeline');
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isTaskMode, setIsTaskMode] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [showPdfMenu, setShowPdfMenu] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showTemplateMenu, setShowTemplateMenu] = useState(false);
  const [templateFilter, setTemplateFilter] = useState('');
  const [selectedTemplateIndex, setSelectedTemplateIndex] = useState(0);
  const [showDateTimeEditor, setShowDateTimeEditor] = useState(false);
  const [customTime, setCustomTime] = useState(format(new Date(), 'HH:mm'));

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [isMobile, setIsMobile] = useState(false);

  // Task enhancement states
  const [showPriorityDialog, setShowPriorityDialog] = useState(false);
  const [showDeadlineDialog, setShowDeadlineDialog] = useState(false);
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [showTaskPropertiesEditor, setShowTaskPropertiesEditor] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskSortMode, setTaskSortMode] = useState('smart'); // 'smart', 'priority', 'deadline', 'date'
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  const [expandedEntries, setExpandedEntries] = useState(new Set());

  // New task properties (for setting when creating)
  const [newTaskPriority, setNewTaskPriority] = useState(null);
  const [newTaskDeadline, setNewTaskDeadline] = useState(null);
  const [newTaskRecurring, setNewTaskRecurring] = useState(null);
  const [tempDeadline, setTempDeadline] = useState('');
  const [tempPriority, setTempPriority] = useState(null);
  const [tempRecurring, setTempRecurring] = useState(null);

  const contentRef = useRef(null);
  const textareaRef = useRef(null);
  const tasksContentRef = useRef(null);
  const calendarContentRef = useRef(null);
  const recognitionRef = useRef(null);
  
  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 1024);
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.min(textareaRef.current.scrollHeight, 120);
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [newEntry]);

  useEffect(() => {
    if (activeTab === 'timeline') {
      // Use setTimeout to ensure DOM has rendered
      setTimeout(() => scrollToBottom('auto'), 50);
    }
  }, [entries]);

  useLayoutEffect(() => {
    // Delay scroll to ensure content is rendered
    const scrollTimer = setTimeout(() => {
      if (activeTab === 'timeline') {
        scrollToBottom('auto');
      } else if (activeTab === 'tasks' && tasksContentRef.current) {
        tasksContentRef.current.scrollTo({ top: tasksContentRef.current.scrollHeight, behavior: 'auto' });
      } else if (activeTab === 'calendar' && calendarContentRef.current) {
        calendarContentRef.current.scrollTo({ top: calendarContentRef.current.scrollHeight, behavior: 'auto' });
      }
    }, 100);

    return () => clearTimeout(scrollTimer);
  }, [activeTab]);

  useEffect(() => {
    const handleScroll = () => {
      if (contentRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
        setShowScrollButton(scrollTop < scrollHeight - clientHeight - 100);
      }
    };

    const contentElement = contentRef.current;
    if (contentElement) {
      contentElement.addEventListener('scroll', handleScroll);
      return () => contentElement.removeEventListener('scroll', handleScroll);
    }
  }, []);
  
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
      setShowUserMenu(false);
      setShowPdfMenu(false);
    };
    if (openMenuId || showUserMenu || showPdfMenu) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [openMenuId, showUserMenu, showPdfMenu]);

  // Auto-enable task mode when in Tasks tab, disable when leaving
  useEffect(() => {
    if (activeTab === 'tasks') {
      setIsTaskMode(true);
    } else if (!editingEntry) {
      // Only auto-disable if not editing (to preserve user's choice during editing)
      setIsTaskMode(false);
    }
  }, [activeTab, editingEntry]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'en-US';

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          setNewEntry(prev => (prev + ' ' + finalTranscript).trim());
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);

        if (event.error === 'not-allowed') {
          alert('Microphone access denied. Please allow microphone access in your browser settings.');
        } else if (event.error === 'no-speech') {
          // Silently handle no speech detected
        } else {
          console.log('Speech recognition error:', event.error);
        }
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  const toggleDictation = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in your browser. Please use Chrome, Safari, or Edge.');
      return;
    }

    if (isRecording) {
      recognitionRef.current.stop();
      setIsRecording(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
      } catch (error) {
        console.error('Error starting recognition:', error);
        setIsRecording(false);
      }
    }
  };

  // Template helper functions
  const replaceTemplateVariables = (content) => {
    const now = new Date();
    const weekStart = new Date(now);
    weekStart.setDate(now.getDate() - now.getDay());
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);

    return content
      .replace('{date}', format(now, 'MMMM d, yyyy'))
      .replace('{time}', format(now, 'h:mm a'))
      .replace('{day}', format(now, 'EEEE'))
      .replace('{week}', `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d')}`)
      .replace('{month}', format(now, 'MMMM yyyy'));
  };

  const insertTemplate = (template) => {
    const processedContent = replaceTemplateVariables(template.content);
    const cursorMarker = '{cursor}';
    const hasCursor = processedContent.includes(cursorMarker);

    if (hasCursor) {
      const beforeCursor = processedContent.substring(0, processedContent.indexOf(cursorMarker));
      const afterCursor = processedContent.substring(processedContent.indexOf(cursorMarker) + cursorMarker.length);
      const finalContent = beforeCursor + afterCursor;

      setNewEntry(finalContent);
      setShowTemplateMenu(false);
      setTemplateFilter('');

      // Position cursor after insertion
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(beforeCursor.length, beforeCursor.length);
        }
      }, 0);
    } else {
      setNewEntry(processedContent);
      setShowTemplateMenu(false);
      setTemplateFilter('');
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.focus();
        }
      }, 0);
    }
  };

  const getFilteredTemplates = () => {
    if (!templateFilter) {
      return TEMPLATES;
    }

    const filter = templateFilter.toLowerCase();
    const filtered = TEMPLATES.filter(template =>
      template.name.toLowerCase().includes(filter) ||
      template.keywords.some(keyword => keyword.includes(filter))
    );
    return filtered;
  };

  const scrollToBottom = (behavior = 'smooth') => {
    if (contentRef.current) {
      contentRef.current.scrollTo({
        top: contentRef.current.scrollHeight,
        behavior: behavior,
      });
    }
  };

  // Extract tasks from markdown checkbox syntax
  const extractTasksFromContent = (content) => {
    const lines = content.split('\n');
    const taskLines = [];
    const remainingLines = [];

    lines.forEach(line => {
      // Match lines starting with - [ ] (with optional whitespace)
      const taskMatch = line.match(/^\s*-\s*\[\s*\]\s*(.+)/);
      if (taskMatch && taskMatch[1].trim()) {
        taskLines.push(taskMatch[1].trim());
      } else {
        remainingLines.push(line);
      }
    });

    return {
      tasks: taskLines,
      remainingContent: remainingLines.join('\n').trim(),
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Stop recording if active
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
    }

    if (newEntry.trim()) {
      if (editingEntry) {
        // Update existing entry
        const result = await updateEntry(editingEntry.id, {
          content: newEntry.trim(),
          type: isTaskMode ? 'todo' : 'note',
        });

        if (result.error) {
          console.error('Update failed:', result.error);
          alert('Failed to update entry. Please try again.');
          return;
        }

        // Clear editing state and input immediately after successful edit
        setEditingEntry(null);
        setNewEntry('');
        if (activeTab !== 'tasks') {
          setIsTaskMode(false);
        }
        if (textareaRef.current) {
          textareaRef.current.style.height = '40px';
        }
        return; // Exit early after edit to prevent duplicate cleanup
      } else {
        // Create new entry
        let timestamp;

        // In calendar tab, use selected date + custom time
        if (activeTab === 'calendar') {
          const [hours, minutes] = customTime.split(':');
          const entryDate = new Date(selectedDate);
          entryDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
          timestamp = entryDate.toISOString();
        } else {
          // In other tabs, use current time
          timestamp = new Date().toISOString();
        }

        // Extract tasks from content if not already in task mode
        const { tasks, remainingContent } = extractTasksFromContent(newEntry.trim());

        // If we found tasks in the content and we're not in task mode, create them separately
        if (tasks.length > 0 && !isTaskMode) {
          // Create the main note entry with remaining content (if any)
          if (remainingContent) {
            const noteData = {
              content: remainingContent,
              sender: 'user',
              type: 'note',
              tags: [],
              timestamp,
            };
            const result = await addEntry(noteData);
            if (result.error) {
              console.error('Failed to create note:', result.error);
              alert('Failed to save entry. Please try again.');
              return;
            }
          }

          // Create individual task entries for each extracted task
          for (const taskContent of tasks) {
            const taskData = {
              content: taskContent,
              sender: 'user',
              type: 'todo',
              completed: false,
              tags: [],
              timestamp,
            };
            const result = await addEntry(taskData);
            if (result.error) {
              console.error('Failed to create task:', result.error);
              alert('Failed to save task. Please try again.');
              return;
            }
          }
        } else {
          // No tasks found, or already in task mode - create single entry as normal
          const entryData = {
            content: newEntry.trim(),
            sender: 'user',
            type: isTaskMode ? 'todo' : 'note',
            completed: isTaskMode ? false : undefined,
            tags: [],
            timestamp,
            // Include new task properties if in task mode
            ...(isTaskMode && {
              priority: newTaskPriority,
              deadline: newTaskDeadline,
              recurring: newTaskRecurring,
            }),
          };
          const result = await addEntry(entryData);
          if (result.error) {
            console.error('Failed to create entry:', result.error);
            alert('Failed to save entry. Please try again.');
            return;
          }
        }
      }

      setNewEntry('');
      // Don't turn off task mode if we're in the tasks tab
      if (activeTab !== 'tasks') {
        setIsTaskMode(false);
      }
      // Clear new task properties after submission
      setNewTaskPriority(null);
      setNewTaskDeadline(null);
      setNewTaskRecurring(null);
      if (textareaRef.current) {
        textareaRef.current.style.height = '40px';
      }
    }
  };
  
  const handleToggleTodo = async (entry) => {
    await toggleTodoCompletion(entry.id, entry.completed);
  };
  
  const handleDeleteEntry = async (id) => {
    await deleteEntry(id);
    setOpenMenuId(null);
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setNewEntry(entry.content);
    setIsTaskMode(entry.type === 'todo');
    setOpenMenuId(null);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setNewEntry('');
    // Clear new task properties
    setNewTaskPriority(null);
    setNewTaskDeadline(null);
    setNewTaskRecurring(null);
    // Don't turn off task mode if we're in the tasks tab
    if (activeTab !== 'tasks') {
      setIsTaskMode(false);
    }
    if (textareaRef.current) {
      textareaRef.current.style.height = '40px';
    }
  };

  // Task enhancement handlers
  const handleSetPriority = async (priority) => {
    if (selectedTask) {
      // Editing existing task
      await updateEntry(selectedTask.id, { priority });
      setShowPriorityDialog(false);
      setSelectedTask(null);
      setOpenMenuId(null);
    } else {
      // Setting priority for new task
      setNewTaskPriority(priority);
      setShowPriorityDialog(false);
    }
  };

  const handleSetDeadline = async (deadline) => {
    if (selectedTask) {
      // Editing existing task
      await updateEntry(selectedTask.id, { deadline: deadline ? new Date(deadline).toISOString() : null });
      setShowDeadlineDialog(false);
      setSelectedTask(null);
      setOpenMenuId(null);
      setTempDeadline('');
    } else {
      // Setting deadline for new task
      setNewTaskDeadline(deadline ? new Date(deadline).toISOString() : null);
      setShowDeadlineDialog(false);
      setTempDeadline('');
    }
  };

  const handleOpenDeadlineDialog = () => {
    // Initialize tempDeadline with current value
    if (selectedTask?.deadline) {
      setTempDeadline(format(new Date(selectedTask.deadline), 'yyyy-MM-dd'));
    } else if (newTaskDeadline) {
      setTempDeadline(format(new Date(newTaskDeadline), 'yyyy-MM-dd'));
    } else {
      // Default to today
      setTempDeadline(format(new Date(), 'yyyy-MM-dd'));
    }
    setShowDeadlineDialog(true);
  };

  const handleSetRecurring = async (recurring) => {
    if (selectedTask) {
      // Editing existing task
      await updateEntry(selectedTask.id, { recurring });
      setShowRecurringDialog(false);
      setSelectedTask(null);
      setOpenMenuId(null);
    } else {
      // Setting recurring for new task
      setNewTaskRecurring(recurring);
      setShowRecurringDialog(false);
    }
  };

  const handleOpenTaskPropertiesEditor = () => {
    // Set temp values to current values
    setTempPriority(newTaskPriority);
    // Default to today's date if no deadline is set
    setTempDeadline(newTaskDeadline ? format(new Date(newTaskDeadline), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'));
    setTempRecurring(newTaskRecurring);
    setShowTaskPropertiesEditor(true);
  };

  const handleSaveTaskProperties = () => {
    setNewTaskPriority(tempPriority);
    setNewTaskDeadline(tempDeadline ? new Date(tempDeadline).toISOString() : null);
    setNewTaskRecurring(tempRecurring);
    setShowTaskPropertiesEditor(false);
  };

  const handleCancelTaskPropertiesEditor = () => {
    setShowTaskPropertiesEditor(false);
  };

  const cycleSortMode = () => {
    const modes = ['smart', 'priority', 'deadline', 'date'];
    const currentIndex = modes.indexOf(taskSortMode);
    const nextIndex = (currentIndex + 1) % modes.length;
    setTaskSortMode(modes[nextIndex]);
  };

  // Read more utilities
  const shouldTruncateEntry = (content) => {
    const lines = content.split('\n');
    return lines.length > 4 || content.length > 300;
  };

  const getTruncatedContent = (content) => {
    const lines = content.split('\n');
    if (lines.length > 4) {
      return lines.slice(0, 4).join('\n');
    }
    if (content.length > 300) {
      return content.substring(0, 300);
    }
    return content;
  };

  const toggleEntryExpansion = (entryId) => {
    setExpandedEntries(prev => {
      const newSet = new Set(prev);
      if (newSet.has(entryId)) {
        newSet.delete(entryId);
      } else {
        newSet.add(entryId);
      }
      return newSet;
    });
  };

  // Utility functions for task features
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#ef4444'; // red
      case 'medium': return '#f97316'; // orange
      case 'low': return '#3b82f6'; // blue
      default: return null;
    }
  };

  const formatDeadline = (deadline) => {
    if (!deadline) return null;
    const deadlineDate = new Date(deadline);
    const now = new Date();
    const diffTime = deadlineDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      const overdueDays = Math.abs(diffDays);
      return { text: `Overdue ${overdueDays} day${overdueDays !== 1 ? 's' : ''}`, color: '#ef4444', isOverdue: true };
    } else if (diffDays === 0) {
      return { text: 'Due today', color: '#f97316', isOverdue: false };
    } else if (diffDays === 1) {
      return { text: 'Due tomorrow', color: '#fbbf24', isOverdue: false };
    } else if (diffDays <= 7) {
      return { text: `Due in ${diffDays} days`, color: '#a3a3a3', isOverdue: false };
    } else {
      return { text: `Due ${format(deadlineDate, 'MMM d')}`, color: '#a3a3a3', isOverdue: false };
    }
  };

  const handleToggleTodoWithRecurring = async (entry) => {
    await handleToggleTodo(entry);

    // If completing a recurring task, create the next instance
    if (!entry.completed && entry.recurring) {
      const nextDate = new Date(entry.timestamp);
      switch (entry.recurring) {
        case 'daily':
          nextDate.setDate(nextDate.getDate() + 1);
          break;
        case 'weekly':
          nextDate.setDate(nextDate.getDate() + 7);
          break;
        case 'monthly':
          nextDate.setMonth(nextDate.getMonth() + 1);
          break;
      }

      await addEntry({
        content: entry.content,
        sender: 'user',
        type: 'todo',
        completed: false,
        tags: entry.tags || [],
        timestamp: nextDate.toISOString(),
        priority: entry.priority,
        deadline: entry.deadline,
        recurring: entry.recurring,
      });
    }
  };

  const handleSignOut = async () => {
    await signOut();
    setShowUserMenu(false);
  };

  const getUserDisplayName = () => {
    if (profile?.first_name || profile?.last_name) {
      return `${profile.first_name} ${profile.last_name}`.trim();
    }
    return user?.email || 'User';
  };

  const generatePDF = (title, dateRange, entriesToExport) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Watermark/Title
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('Mindjar Entries', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // User name
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    const userName = getUserDisplayName();
    doc.text(userName, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;

    // Date range
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(dateRange, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Separator line
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Separate entries into notes and tasks
    const notes = entriesToExport.filter(e => e.type === 'note');
    const tasks = entriesToExport.filter(e => e.type === 'todo');

    // Notes section
    if (notes.length > 0) {
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Notes', margin, yPosition);
      yPosition += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      notes.forEach((entry, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = margin;
        }

        // Date
        doc.setTextColor(100, 100, 100);
        const entryDate = format(entry.timestamp, 'MMM d, yyyy h:mm a');
        doc.text(entryDate, margin, yPosition);
        yPosition += 6;

        // Content
        doc.setTextColor(0, 0, 0);
        const lines = doc.splitTextToSize(entry.content, pageWidth - (margin * 2));
        lines.forEach(line => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += 5;
        });

        yPosition += 5; // Space between entries
      });

      yPosition += 5;
    }

    // Tasks section
    if (tasks.length > 0) {
      // Check if we need a new page
      if (yPosition > pageHeight - 30) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 0, 0);
      doc.text('Tasks', margin, yPosition);
      yPosition += 8;

      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');

      tasks.forEach((entry, index) => {
        // Check if we need a new page
        if (yPosition > pageHeight - 30) {
          doc.addPage();
          yPosition = margin;
        }

        // Checkbox
        const checkboxSize = 4;
        doc.setDrawColor(0, 0, 0);
        doc.rect(margin, yPosition - 3, checkboxSize, checkboxSize);

        // Checkmark if completed
        if (entry.completed) {
          doc.setFontSize(12);
          doc.text('✓', margin + 0.5, yPosition);
          doc.setFontSize(10);
        }

        // Date
        doc.setTextColor(100, 100, 100);
        const entryDate = format(entry.timestamp, 'MMM d, yyyy');
        doc.text(entryDate, margin + checkboxSize + 3, yPosition);
        yPosition += 6;

        // Content
        doc.setTextColor(0, 0, 0);
        const lines = doc.splitTextToSize(entry.content, pageWidth - (margin * 2) - 10);
        lines.forEach(line => {
          if (yPosition > pageHeight - 30) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin + checkboxSize + 3, yPosition);
          yPosition += 5;
        });

        yPosition += 5; // Space between entries
      });
    }

    // If no entries
    if (notes.length === 0 && tasks.length === 0) {
      doc.setTextColor(100, 100, 100);
      doc.text('No entries found for this period', pageWidth / 2, yPosition, { align: 'center' });
    }

    // Save the PDF
    const fileName = `MyMotif_${title.replace(/ /g, '_')}.pdf`;
    doc.save(fileName);
  };

  const handleDownloadMonth = () => {
    const monthStart = startOfMonth(calendarMonth);
    const monthEnd = endOfMonth(calendarMonth);

    const monthEntries = entries.filter(entry => {
      const entryDate = entry.timestamp;
      return entryDate >= monthStart && entryDate <= monthEnd;
    });

    const monthName = format(calendarMonth, 'MMMM yyyy');
    generatePDF(monthName, monthName, monthEntries);
    setShowPdfMenu(false);
  };

  const handleDownloadDay = () => {
    const dayEntries = entries.filter(entry =>
      isSameDay(entry.timestamp, selectedDate)
    );

    const dayName = format(selectedDate, 'MMMM d, yyyy');
    generatePDF(dayName, dayName, dayEntries);
    setShowPdfMenu(false);
  };

  const formatTimestamp = (timestamp) => {
    return format(timestamp, 'h:mm a');
  };

  const formatDateSeparator = (timestamp) => {
    if (isToday(timestamp)) return 'Today';
    if (isYesterday(timestamp)) return 'Yesterday';
    return format(timestamp, 'MMMM d, yyyy');
  };

  const groupEntriesByDate = (entries) => {
    return entries.reduce((acc, entry) => {
      const dateKey = startOfDay(entry.timestamp).toISOString();
      if (!acc[dateKey]) {
        acc[dateKey] = {
          date: entry.timestamp,
          entries: []
        };
      }
      acc[dateKey].entries.push(entry);
      return acc;
    }, {});
  };

  const renderEntryBubble = (entry, showDateHeader = false, dateText = '') => {
    const isUser = entry.sender === 'user';
    const isTodo = entry.type === 'todo';

    return (
      <div key={entry.id}>
        {showDateHeader && (
          <div className="sticky top-2 z-10 flex justify-center py-2 mb-4">
            <span className="bg-[#141414] text-xs text-[#a3a3a3] font-semibold px-3 py-1 rounded-full border border-[#262626]">
              {dateText}
            </span>
          </div>
        )}
        <motion.div
          layout
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} mb-4`}
        >
          <div className={`${isUser ? 'bg-[#3b82f6]' : 'bg-[#262626]'} text-white rounded-2xl ${isUser ? 'rounded-tr-none' : 'rounded-tl-none'} p-4 w-full lg:w-[80%] shadow-md`}>
            <div className="flex items-start gap-3">
              {!isUser && <SafeIcon icon={FiCpu} className="text-xl text-[#a3a3a3] flex-shrink-0 mt-1" />}
              
              {isTodo && (
                <button
                  onClick={() => handleToggleTodoWithRecurring(entry)}
                  className="flex-shrink-0 mt-0.5"
                >
                  <SafeIcon
                    icon={entry.completed ? FiCheckSquare : FiSquare}
                    className={`text-xl ${entry.completed ? 'text-white' : 'text-white/70'}`}
                  />
                </button>
              )}
              
              <div className="flex-1 min-w-0">
                {isUser && entry.tags && entry.tags.length > 0 && (
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {entry.tags.map(tagKey => {
                      const tagConfig = availableTags.find(t => t.key === tagKey);
                      return (
                        <span
                          key={tagKey}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[rgba(255,255,255,0.15)] rounded-full text-xs font-medium"
                        >
                          <SafeIcon icon={tagConfig?.icon || FiHash} className="text-xs opacity-80" />
                          {tagConfig?.label || tagKey}
                        </span>
                      );
                    })}
                  </div>
                )}

                <div className={`text-[15px] leading-relaxed whitespace-pre-wrap break-words ${isTodo && entry.completed ? 'line-through opacity-75' : ''}`}>
                  {shouldTruncateEntry(entry.content) && !expandedEntries.has(entry.id)
                    ? getTruncatedContent(entry.content)
                    : entry.content
                  }
                </div>
                {shouldTruncateEntry(entry.content) && (
                  <button
                    onClick={() => toggleEntryExpansion(entry.id)}
                    className="text-xs text-white/60 hover:text-white/90 mt-1 font-medium transition-colors"
                  >
                    {expandedEntries.has(entry.id) ? 'Read less' : 'Read more'}
                  </button>
                )}
              </div>
              
              <div className="relative flex-shrink-0">
                <button 
                  onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === entry.id ? null : entry.id); }}
                  className="text-white/50 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiMoreHorizontal} className="text-lg" />
                </button>
                <AnimatePresence>
                  {openMenuId === entry.id && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-7 right-0 bg-[#2a2a2a] border border-[#404040] rounded-lg shadow-xl w-32 z-20"
                    >
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditEntry(entry); }}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a] rounded-t-lg">
                        <SafeIcon icon={FiEdit} className="text-sm" /> Edit
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteEntry(entry.id); }}
                        className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-red-400 hover:bg-[#3a3a3a] rounded-b-lg">
                        <SafeIcon icon={FiTrash2} className="text-sm" /> Delete
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
          <div className="text-[11px] text-[#737373] mt-1.5 px-2">
            {formatTimestamp(entry.timestamp)}
          </div>
        </motion.div>
      </div>
    );
  };
  
  const sortedEntries = [...entries].sort((a, b) => {
    const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
    const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
    return timeA - timeB;
  });

  // Limit timeline to 50 most recent entries
  const TIMELINE_LIMIT = 50;
  const hasMoreEntries = sortedEntries.length > TIMELINE_LIMIT;
  const timelineEntries = sortedEntries.slice(-TIMELINE_LIMIT);

  const groupedTimelineEntries = groupEntriesByDate(timelineEntries);
  const timelineDates = Object.keys(groupedTimelineEntries);

  const TasksView = () => {
    const todoEntries = sortedEntries.filter(e => e.type === 'todo');

    // Separate active and completed tasks
    const activeTasks = todoEntries.filter(e => !e.completed);
    const completedTasks = todoEntries.filter(e => e.completed);

    // Smart sorting function
    const sortTasks = (tasks) => {
      return [...tasks].sort((a, b) => {
        if (taskSortMode === 'smart') {
          // Priority first (high > medium > low > none)
          const priorityOrder = { high: 0, medium: 1, low: 2, null: 3, undefined: 3 };
          const aPriority = priorityOrder[a.priority] ?? 3;
          const bPriority = priorityOrder[b.priority] ?? 3;
          if (aPriority !== bPriority) return aPriority - bPriority;

          // Then by deadline (earlier first, nulls last)
          if (a.deadline && b.deadline) {
            return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
          }
          if (a.deadline) return -1;
          if (b.deadline) return 1;

          // Finally by date created
          const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
          return timeB - timeA;
        } else if (taskSortMode === 'priority') {
          const priorityOrder = { high: 0, medium: 1, low: 2, null: 3, undefined: 3 };
          const aPriority = priorityOrder[a.priority] ?? 3;
          const bPriority = priorityOrder[b.priority] ?? 3;
          return aPriority - bPriority;
        } else if (taskSortMode === 'deadline') {
          if (a.deadline && b.deadline) {
            return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
          }
          if (a.deadline) return -1;
          if (b.deadline) return 1;
          return 0;
        } else {
          // date mode
          const timeA = a.timestamp instanceof Date ? a.timestamp.getTime() : new Date(a.timestamp).getTime();
          const timeB = b.timestamp instanceof Date ? b.timestamp.getTime() : new Date(b.timestamp).getTime();
          return timeB - timeA;
        }
      });
    };

    const sortedActiveTasks = sortTasks(activeTasks);
    const sortedCompletedTasks = sortTasks(completedTasks);

    const renderTask = (entry) => {
      const priorityColor = getPriorityColor(entry.priority);
      const deadlineInfo = formatDeadline(entry.deadline);

      return (
        <motion.div
          key={entry.id}
          layout
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          style={{
            borderLeftWidth: priorityColor ? '3px' : '0px',
            borderLeftColor: priorityColor || 'transparent',
          }}
          className="relative flex items-start gap-3 p-4 bg-[#141414] rounded-xl border border-[#262626] mb-3"
        >
          <button onClick={() => handleToggleTodoWithRecurring(entry)}>
            <SafeIcon
              icon={entry.completed ? FiCheckSquare : FiSquare}
              className={`text-xl mt-0.5 ${entry.completed ? 'text-[#3b82f6]' : 'text-[#a3a3a3]'}`}
            />
          </button>
          <div className="flex-1 min-w-0">
            <span className={`block text-[15px] ${entry.completed ? 'line-through text-[#737373]' : 'text-white'}`}>
              {entry.content}
            </span>
            {deadlineInfo && !entry.completed && (
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs font-medium" style={{ color: deadlineInfo.color }}>
                  {deadlineInfo.text}
                </span>
                {entry.recurring && (
                  <span className="text-xs text-[#a3a3a3]">
                    • {entry.recurring}
                  </span>
                )}
              </div>
            )}
            {entry.recurring && entry.completed && (
              <span className="text-xs text-[#737373] mt-1 block">
                Recurring: {entry.recurring}
              </span>
            )}
          </div>

          <div className="relative flex-shrink-0">
            <button
              onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === entry.id ? null : entry.id); }}
              className="text-white/50 hover:text-white transition-colors"
            >
              <SafeIcon icon={FiMoreHorizontal} className="text-lg" />
            </button>
            <AnimatePresence>
              {openMenuId === entry.id && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-7 right-0 bg-[#2a2a2a] border border-[#404040] rounded-lg shadow-xl w-40 z-20"
                >
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedTask(entry); setShowPriorityDialog(true); }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a] rounded-t-lg">
                    <SafeIcon icon={FiAward} className="text-sm" /> Priority
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedTask(entry); handleOpenDeadlineDialog(); }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a]">
                    <SafeIcon icon={FiCalendar} className="text-sm" /> Deadline
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setSelectedTask(entry); setShowRecurringDialog(true); }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a]">
                    <SafeIcon icon={FiCpu} className="text-sm" /> Recurring
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleEditEntry(entry); }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a]">
                    <SafeIcon icon={FiEdit} className="text-sm" /> Edit
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteEntry(entry.id); }}
                    className="w-full text-left px-3 py-2 text-sm flex items-center gap-2 text-red-400 hover:bg-[#3a3a3a] rounded-b-lg">
                    <SafeIcon icon={FiTrash2} className="text-sm" /> Delete
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      );
    };

    return (
      <>
        {todoEntries.length > 0 ? (
          <div className="space-y-6">
            {/* Active Tasks Section */}
            {sortedActiveTasks.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-[#a3a3a3] uppercase tracking-wide">
                    Active ({sortedActiveTasks.length})
                  </h3>
                  <button
                    onClick={cycleSortMode}
                    className="flex items-center gap-1.5 px-2 py-1 text-xs text-[#a3a3a3] hover:text-white hover:bg-[#262626] rounded-lg transition-colors"
                    title={`Sort: ${taskSortMode}`}
                  >
                    <SafeIcon icon={FiArrowDown} className="text-sm" />
                    {taskSortMode}
                  </button>
                </div>
                <div className="space-y-2">
                  {sortedActiveTasks.map(entry => renderTask(entry))}
                </div>
              </div>
            )}

            {/* Completed Tasks Section */}
            {sortedCompletedTasks.length > 0 && (
              <div>
                <button
                  onClick={() => setShowCompletedTasks(!showCompletedTasks)}
                  className="w-full flex items-center justify-between mb-3 text-left hover:bg-[#1a1a1a] p-2 rounded-lg transition-colors"
                >
                  <h3 className="text-sm font-semibold text-[#737373] uppercase tracking-wide">
                    Completed ({sortedCompletedTasks.length})
                  </h3>
                  <SafeIcon
                    icon={FiChevronDown}
                    className={`text-[#737373] transition-transform ${showCompletedTasks ? 'rotate-180' : ''}`}
                  />
                </button>
                <AnimatePresence>
                  {showCompletedTasks && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="space-y-2 overflow-hidden"
                    >
                      {sortedCompletedTasks.map(entry => renderTask(entry))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center min-h-[40vh] flex flex-col items-center justify-center py-12">
            <SafeIcon icon={FiClipboard} className="text-6xl text-[#404040] mx-auto mb-4 opacity-50" />
            <div className="text-lg text-[#a3a3a3] font-medium mb-2">No tasks found</div>
            <div className="text-sm text-[#737373]">Click the checkbox icon when adding an entry to create a task</div>
          </div>
        )}
      </>
    );
  };

  const CalendarView = () => {
    const daysInMonth = eachDayOfInterval({
      start: startOfMonth(calendarMonth),
      end: endOfMonth(calendarMonth),
    });
  
    const startingDayIndex = startOfMonth(calendarMonth).getDay();
    const paddedDays = Array(startingDayIndex).fill(null).concat(daysInMonth);
  
    const getEntriesForDate = (date) => entries.filter(entry => isSameDay(entry.timestamp, date));
    
    const entriesForSelectedDate = getEntriesForDate(selectedDate);
    const groupedEntriesForSelectedDate = groupEntriesByDate(entriesForSelectedDate);
    const entryDatesForSelectedDate = Object.keys(groupedEntriesForSelectedDate);

    const handleDateSelect = (date) => {
      setSelectedDate(date);
    };

    const handleTodayClick = () => {
      const today = new Date();
      setSelectedDate(today);
      setCalendarMonth(today);
    };

    const CalendarGrid = () => (
      <div className="bg-[#141414] border border-[#262626] rounded-xl p-4 shadow-md w-full">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-white">
            {format(calendarMonth, 'MMMM yyyy')}
          </h3>
          <div className="flex gap-1">
            <button
              onClick={() => setCalendarMonth(subMonths(calendarMonth, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-transparent hover:bg-[#1a1a1a] transition-colors"
            >
              <SafeIcon icon={FiChevronLeft} className="text-white" />
            </button>
            <button
              onClick={() => setCalendarMonth(addMonths(calendarMonth, 1))}
              className="w-8 h-8 flex items-center justify-center rounded-lg bg-transparent hover:bg-[#1a1a1a] transition-colors"
            >
              <SafeIcon icon={FiChevronRight} className="text-white" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-7 gap-1 mb-2">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((day) => (
            <div key={day} className="text-center text-xs text-[#737373] font-medium py-1">
              {day}
            </div>
          ))}
        </div>
        
        <div className="grid grid-cols-7 gap-1">
          {paddedDays.map((day, index) => {
            if (!day) return <div key={`empty-${index}`} className="h-10" />;
            
            const isSelected = isSameDay(day, selectedDate);
            const isCurrentMonthToday = isToday(day);
            const hasEntries = getEntriesForDate(day).length > 0;
            
            return (
              <button
                key={day.toString()}
                onClick={() => handleDateSelect(day)}
                className={`h-10 rounded-lg flex flex-col items-center justify-center relative transition-all text-sm
                  ${isSelected 
                    ? 'bg-[#3b82f6] text-white font-semibold' 
                    : isCurrentMonthToday
                      ? 'bg-[rgba(59,130,246,0.15)] text-[#3b82f6] font-semibold' 
                      : 'text-white hover:bg-[#1a1a1a]'}
                  ${!isSameMonth(day, calendarMonth) && !isSelected ? 'text-[#404040]' : ''}
                `}
              >
                {format(day, 'd')}
                {hasEntries && !isSelected && (
                  <div className="absolute bottom-1.5 w-1 h-1 rounded-full bg-[#3b82f6]"></div>
                )}
                {hasEntries && isSelected && (
                  <div className="absolute bottom-1.5 w-1 h-1 rounded-full bg-white"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );

    return (
      <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
        <div className="w-full lg:w-[320px] flex-shrink-0">
          <div className="lg:sticky top-6">
            <div className="mb-4 flex items-center gap-2">
              <button
                onClick={handleTodayClick}
                className="flex-1 py-2 bg-transparent border border-[#404040] text-white rounded-lg text-sm font-medium hover:bg-[#1a1a1a] hover:border-[#525252] transition-all"
              >
                Today
              </button>
              <div className="relative flex-1">
                <button
                  onClick={(e) => { e.stopPropagation(); setShowPdfMenu(!showPdfMenu); }}
                  className="w-full py-2 bg-[#3b82f6] text-white rounded-lg text-sm font-medium hover:bg-[#2563eb] transition-all flex items-center justify-center gap-2"
                >
                  <SafeIcon icon={FiDownload} className="text-sm" />
                  Export
                </button>
                <AnimatePresence>
                  {showPdfMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      onClick={(e) => e.stopPropagation()}
                      className="absolute top-12 right-0 bg-[#2a2a2a] border border-[#404040] rounded-lg shadow-xl w-48 z-20"
                    >
                      <button
                        onClick={handleDownloadMonth}
                        className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-[#3a3a3a] rounded-t-lg"
                      >
                        Download Month
                      </button>
                      <button
                        onClick={handleDownloadDay}
                        className="w-full text-left px-4 py-2.5 text-sm text-white hover:bg-[#3a3a3a] rounded-b-lg"
                      >
                        Download Current Day
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <CalendarGrid />
          </div>
        </div>
        
        <div ref={calendarContentRef} className="flex-1 lg:max-h-[calc(100vh-280px)] lg:overflow-y-auto">
          <div className="space-y-2 pb-10">
            {entryDatesForSelectedDate.length > 0 ? (
              entryDatesForSelectedDate.map((dateKey) => (
                <div key={dateKey}>
                  {groupedEntriesForSelectedDate[dateKey].entries.map((entry, entryIndex) => {
                    const isFirstEntryOfDate = entryIndex === 0;
                    return renderEntryBubble(
                      entry, 
                      isFirstEntryOfDate, 
                      formatDateSeparator(groupedEntriesForSelectedDate[dateKey].date)
                    );
                  })}
                </div>
              ))
            ) : (
              <div className="text-center min-h-[40vh] flex flex-col items-center justify-center">
                <SafeIcon icon={FiCalendar} className="text-6xl text-[#404040] mx-auto mb-4 opacity-50" />
                <div className="text-lg text-[#a3a3a3] font-medium mb-2">No entries for this date</div>
                <div className="text-sm text-[#737373]">Use the input below to add your first entry</div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Auth onSignIn={signIn} onSignUp={signUp} />;
  }

  const hasContent = newEntry.trim().length > 0;

  return (
    <div className="fixed inset-0 bg-[#0a0a0a] text-white font-sans overflow-hidden">
      <header className="fixed top-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-lg border-b border-[#262626] z-50 flex flex-col items-center">
        <div className="flex items-center justify-between w-full max-w-[800px] h-16 px-6 mx-auto">
          <span className="font-bold text-white text-lg">Mindjar</span>
          <div className="relative">
            <button
              onClick={(e) => { e.stopPropagation(); setShowUserMenu(!showUserMenu); }}
              className="flex items-center gap-2 text-sm font-medium cursor-pointer hover:text-[#a3a3a3] transition-colors"
            >
              <span>{getUserDisplayName()}</span>
              <SafeIcon icon={FiChevronDown} />
            </button>
            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-10 right-0 bg-[#2a2a2a] border border-[#404040] rounded-lg shadow-xl w-40 z-20"
                >
                  <button
                    onClick={() => {
                      setShowAnalytics(true);
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a] rounded-t-lg"
                  >
                    <SafeIcon icon={FiBarChart2} className="text-sm" /> Analytics
                  </button>
                  <button
                    onClick={() => {
                      setShowSettings(true);
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a]"
                  >
                    <SafeIcon icon={FiSettings} className="text-sm" /> Settings
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 text-white hover:bg-[#3a3a3a] rounded-b-lg"
                  >
                    <SafeIcon icon={FiLogOut} className="text-sm" /> Sign Out
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
        <div className="hidden md:flex w-full max-w-[800px] justify-center pb-3 px-6">
          <div className="flex items-center bg-[#141414] rounded-full p-1 border border-[#262626] w-full">
            {['timeline', 'calendar', 'tasks'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="relative flex-1 py-1.5 text-sm font-medium transition-colors capitalize"
              >
                {activeTab === tab && (
                  <motion.div
                    layoutId="activeTabPill"
                    className="absolute inset-0 bg-white rounded-full"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <span className={`relative z-10 transition-colors ${activeTab === tab ? 'text-black' : 'text-[#a3a3a3] hover:text-white'}`}>
                  {tab}
                </span>
              </button>
            ))}
          </div>
        </div>
      </header>

      <main
        ref={contentRef}
        className={`absolute left-0 right-0 bottom-[112px] overflow-y-auto overflow-x-hidden ${isMobile ? 'top-[112px]' : 'top-[120px]'}`}
      >
        {isMobile && (
          <div className="fixed top-16 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-lg z-40 border-b border-[#262626] h-14">
            <div className="flex justify-around items-center h-full max-w-[800px] mx-auto px-4">
              {['timeline', 'calendar', 'tasks'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`relative w-full py-2 text-sm font-medium transition-colors capitalize ${activeTab === tab ? 'text-white' : 'text-[#a3a3a3]'}`}
                >
                  {tab}
                  {activeTab === tab && (
                    <motion.div
                      layoutId="activeMobileTab"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-white"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          {entriesLoading ? (
            <div className="w-full max-w-[800px] mx-auto px-6 py-12 text-center">
              <div className="text-[#a3a3a3]">Loading entries...</div>
            </div>
          ) : (
            <>
              {activeTab === 'timeline' && (
                <div className="w-full max-w-[800px] mx-auto px-6">
                  <div className="space-y-2 py-6">
                    {hasMoreEntries && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-[#1a1a1a] border border-[#3b82f6]/30 rounded-xl"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="text-sm text-white font-medium mb-1">
                              Showing latest {TIMELINE_LIMIT} entries
                            </div>
                            <div className="text-xs text-[#a3a3a3]">
                              View older messages in the Calendar tab
                            </div>
                          </div>
                          <button
                            onClick={() => setActiveTab('calendar')}
                            className="px-4 py-2 bg-[#3b82f6] text-white text-sm font-medium rounded-lg hover:bg-[#2563eb] transition-colors flex items-center gap-2 whitespace-nowrap"
                          >
                            <SafeIcon icon={FiCalendar} className="text-sm" />
                            Calendar
                          </button>
                        </div>
                      </motion.div>
                    )}
                    {timelineDates.length > 0 ? (
                      timelineDates.map(dateKey => (
                        <div key={dateKey}>
                          {groupedTimelineEntries[dateKey].entries.map((entry, entryIndex) => {
                            const isFirstEntryOfDate = entryIndex === 0;
                            return renderEntryBubble(
                              entry,
                              isFirstEntryOfDate,
                              formatDateSeparator(groupedTimelineEntries[dateKey].date)
                            );
                          })}
                        </div>
                      ))
                    ) : (
                      <div className="text-center min-h-[40vh] flex flex-col items-center justify-center">
                        <SafeIcon icon={FiCalendar} className="text-6xl text-[#404040] mx-auto mb-4 opacity-50" />
                        <div className="text-lg text-[#a3a3a3] font-medium mb-2">No entries yet</div>
                        <div className="text-sm text-[#737373]">Start journaling by typing below</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'calendar' && (
                <div className="w-full max-w-[800px] mx-auto px-6 py-6">
                  <CalendarView />
                </div>
              )}

              {activeTab === 'tasks' && (
                <div
                  ref={tasksContentRef}
                  className="w-full max-w-[800px] mx-auto px-6 py-6"
                >
                  <TasksView />
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <AnimatePresence>
        {showScrollButton && activeTab === 'timeline' && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            onClick={() => scrollToBottom()}
            className="fixed bottom-28 right-4 md:right-6 w-10 h-10 rounded-full bg-white/10 backdrop-blur-lg border border-white/20 text-white flex items-center justify-center z-40 hover:bg-white/20 transition-colors"
          >
            <SafeIcon icon={FiArrowDown} className="text-lg" />
          </motion.button>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSettings && (
          <UserSettings
            user={user}
            profile={profile}
            onClose={() => setShowSettings(false)}
            onUpdateProfile={updateProfile}
            onUpdatePassword={updatePassword}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAnalytics && (
          <Analytics
            onClose={() => setShowAnalytics(false)}
            entries={entries}
          />
        )}
      </AnimatePresence>

      {/* Date/Time Editor Modal */}
      <AnimatePresence>
        {showDateTimeEditor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowDateTimeEditor(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1a1a] border border-[#404040] rounded-xl p-6 w-full max-w-sm"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Set Entry Date & Time</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#a3a3a3] mb-2">
                    Date
                  </label>
                  <input
                    type="date"
                    value={format(selectedDate, 'yyyy-MM-dd')}
                    onChange={(e) => setSelectedDate(new Date(e.target.value))}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#404040] rounded-lg text-white focus:outline-none focus:border-[#3b82f6] transition-colors"
                    style={{ fontSize: '16px' }}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#a3a3a3] mb-2">
                    Time
                  </label>
                  <input
                    type="time"
                    value={customTime}
                    onChange={(e) => setCustomTime(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#404040] rounded-lg text-white focus:outline-none focus:border-[#3b82f6] transition-colors"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowDateTimeEditor(false)}
                  className="flex-1 px-4 py-2 bg-transparent border border-[#404040] text-white rounded-lg hover:bg-[#262626] transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => setShowDateTimeEditor(false)}
                  className="flex-1 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Priority Dialog */}
      <AnimatePresence>
        {showPriorityDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setShowPriorityDialog(false); setSelectedTask(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1a1a] border border-[#404040] rounded-xl p-6 w-full max-w-sm"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Set Priority</h3>

              <div className="space-y-2">
                <button
                  onClick={() => handleSetPriority('high')}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-[#0a0a0a] border-2 border-[#ef4444] rounded-lg text-white hover:bg-[#1a1a1a] transition-colors"
                >
                  <div className="w-1 h-8 bg-[#ef4444] rounded-full"></div>
                  <span className="font-medium">High Priority</span>
                </button>
                <button
                  onClick={() => handleSetPriority('medium')}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-[#0a0a0a] border-2 border-[#f97316] rounded-lg text-white hover:bg-[#1a1a1a] transition-colors"
                >
                  <div className="w-1 h-8 bg-[#f97316] rounded-full"></div>
                  <span className="font-medium">Medium Priority</span>
                </button>
                <button
                  onClick={() => handleSetPriority('low')}
                  className="w-full flex items-center gap-3 px-4 py-3 bg-[#0a0a0a] border-2 border-[#3b82f6] rounded-lg text-white hover:bg-[#1a1a1a] transition-colors"
                >
                  <div className="w-1 h-8 bg-[#3b82f6] rounded-full"></div>
                  <span className="font-medium">Low Priority</span>
                </button>
                <button
                  onClick={() => handleSetPriority(null)}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border-2 border-[#404040] rounded-lg text-[#a3a3a3] hover:bg-[#1a1a1a] hover:text-white transition-colors"
                >
                  No Priority
                </button>
              </div>

              <button
                type="button"
                onClick={() => { setShowPriorityDialog(false); setSelectedTask(null); }}
                className="w-full mt-4 px-4 py-2 bg-transparent border border-[#404040] text-white rounded-lg hover:bg-[#262626] transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Deadline Dialog */}
      <AnimatePresence>
        {showDeadlineDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setShowDeadlineDialog(false); setSelectedTask(null); setTempDeadline(''); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1a1a] border border-[#404040] rounded-xl p-6 w-full max-w-sm"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Set Deadline</h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#a3a3a3] mb-2">
                    Deadline Date
                  </label>
                  <input
                    type="date"
                    value={tempDeadline}
                    onChange={(e) => setTempDeadline(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#404040] rounded-lg text-white focus:outline-none focus:border-[#3b82f6] transition-colors"
                    style={{ fontSize: '16px' }}
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => { setShowDeadlineDialog(false); setSelectedTask(null); setTempDeadline(''); }}
                  className="flex-1 px-4 py-2 bg-transparent border border-[#404040] text-white rounded-lg hover:bg-[#262626] transition-colors"
                >
                  Cancel
                </button>
                {selectedTask && (selectedTask.deadline || newTaskDeadline) && (
                  <button
                    type="button"
                    onClick={() => handleSetDeadline(null)}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Remove
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleSetDeadline(tempDeadline)}
                  disabled={!tempDeadline}
                  className="flex-1 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#2563eb] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Save
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Recurring Dialog */}
      <AnimatePresence>
        {showRecurringDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => { setShowRecurringDialog(false); setSelectedTask(null); }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#1a1a1a] border border-[#404040] rounded-xl p-6 w-full max-w-sm"
            >
              <h3 className="text-lg font-semibold text-white mb-4">Set Recurring</h3>

              <div className="space-y-2">
                <button
                  onClick={() => handleSetRecurring('daily')}
                  className="w-full text-left px-4 py-3 bg-[#0a0a0a] border border-[#404040] rounded-lg text-white hover:bg-[#1a1a1a] hover:border-[#3b82f6] transition-colors"
                >
                  <div className="font-medium">Daily</div>
                  <div className="text-xs text-[#a3a3a3] mt-1">Repeats every day</div>
                </button>
                <button
                  onClick={() => handleSetRecurring('weekly')}
                  className="w-full text-left px-4 py-3 bg-[#0a0a0a] border border-[#404040] rounded-lg text-white hover:bg-[#1a1a1a] hover:border-[#3b82f6] transition-colors"
                >
                  <div className="font-medium">Weekly</div>
                  <div className="text-xs text-[#a3a3a3] mt-1">Repeats every 7 days</div>
                </button>
                <button
                  onClick={() => handleSetRecurring('monthly')}
                  className="w-full text-left px-4 py-3 bg-[#0a0a0a] border border-[#404040] rounded-lg text-white hover:bg-[#1a1a1a] hover:border-[#3b82f6] transition-colors"
                >
                  <div className="font-medium">Monthly</div>
                  <div className="text-xs text-[#a3a3a3] mt-1">Repeats every month</div>
                </button>
                <button
                  onClick={() => handleSetRecurring(null)}
                  className="w-full px-4 py-3 bg-[#0a0a0a] border border-[#404040] rounded-lg text-[#a3a3a3] hover:bg-[#1a1a1a] hover:text-white transition-colors"
                >
                  No Recurring
                </button>
              </div>

              <button
                type="button"
                onClick={() => { setShowRecurringDialog(false); setSelectedTask(null); }}
                className="w-full mt-4 px-4 py-2 bg-transparent border border-[#404040] text-white rounded-lg hover:bg-[#262626] transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline Task Properties Editor - Compact horizontal layout */}
      <AnimatePresence>
        {showTaskPropertiesEditor && isTaskMode && !editingEntry && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="fixed left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-lg border-t border-[#262626] z-[45] max-h-[50vh] overflow-y-auto"
            style={{ bottom: 'calc(16px + env(safe-area-inset-bottom, 0px) + 170px)' }}
          >
            <div className="w-full max-w-[800px] mx-auto px-4 py-3 md:py-4">
              <div className="bg-[#1a1a1a] border border-[#404040] rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                  {/* Priority - Horizontal pills */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#737373] mb-1.5">Priority</label>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setTempPriority('high')}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempPriority === 'high'
                            ? 'bg-[#ef4444] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        H
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempPriority('medium')}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempPriority === 'medium'
                            ? 'bg-[#f97316] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        M
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempPriority('low')}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempPriority === 'low'
                            ? 'bg-[#3b82f6] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        L
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempPriority(null)}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempPriority === null
                            ? 'bg-[#262626] border border-[#3b82f6] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        -
                      </button>
                    </div>
                  </div>

                  {/* Deadline - Compact input */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#737373] mb-1.5">Deadline</label>
                    <input
                      type="date"
                      value={tempDeadline}
                      onChange={(e) => setTempDeadline(e.target.value)}
                      className="w-full px-2 py-1.5 md:py-1 bg-[#0a0a0a] border border-[#404040] rounded text-white text-[11px] focus:outline-none focus:border-[#3b82f6] transition-colors"
                    />
                  </div>

                  {/* Recurring - Horizontal pills */}
                  <div>
                    <label className="block text-[10px] uppercase tracking-wide text-[#737373] mb-1.5">Recurring</label>
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setTempRecurring('daily')}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempRecurring === 'daily'
                            ? 'bg-[#3b82f6] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        D
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempRecurring('weekly')}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempRecurring === 'weekly'
                            ? 'bg-[#3b82f6] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        W
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempRecurring('monthly')}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempRecurring === 'monthly'
                            ? 'bg-[#3b82f6] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        M
                      </button>
                      <button
                        type="button"
                        onClick={() => setTempRecurring(null)}
                        className={`flex-1 px-2 py-1.5 md:py-1 rounded text-[11px] font-medium transition-colors ${
                          tempRecurring === null
                            ? 'bg-[#262626] border border-[#3b82f6] text-white'
                            : 'bg-[#0a0a0a] text-[#a3a3a3] border border-[#404040]'
                        }`}
                      >
                        -
                      </button>
                    </div>
                  </div>
                </div>

                {/* Action buttons - Small and compact */}
                <div className="flex gap-2 mt-3 justify-end">
                  <button
                    type="button"
                    onClick={handleCancelTaskPropertiesEditor}
                    className="px-3 py-1.5 md:py-1 text-[11px] text-[#a3a3a3] hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveTaskProperties}
                    className="px-3 py-1.5 md:py-1 bg-[#3b82f6] text-white rounded text-[11px] hover:bg-[#2563eb] transition-colors font-medium"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="fixed bottom-0 left-0 right-0 bg-[#0a0a0a]/95 backdrop-blur-lg border-t border-[#262626] z-50 overflow-visible">
        <div className="w-full max-w-[800px] mx-auto p-4 pb-safe relative overflow-visible">
          {editingEntry && (
            <div className="mb-2 px-3 py-2 bg-[#1a1a1a] border border-[#3b82f6] rounded-lg flex items-center justify-between">
              <span className="text-xs text-[#3b82f6] font-medium">Editing entry</span>
              <button
                type="button"
                onClick={handleCancelEdit}
                className="flex items-center gap-1 text-xs text-[#a3a3a3] hover:text-white transition-colors"
              >
                <SafeIcon icon={FiX} className="text-sm" />
                Cancel
              </button>
            </div>
          )}

          {/* Date/Time selector for Calendar tab */}
          {activeTab === 'calendar' && !editingEntry && (
            <div className="mb-2 px-3 py-2 bg-[#1a1a1a] border border-[#262626] rounded-lg flex items-center justify-between">
              <span className="text-xs text-white">
                Entry for: <span className="font-medium">{format(selectedDate, 'MMM d, yyyy')}</span>
                {' • '}
                <span className="font-medium">{format(new Date(`2000-01-01T${customTime}`), 'h:mm a')}</span>
              </span>
              <button
                type="button"
                onClick={() => setShowDateTimeEditor(true)}
                className="text-xs text-[#3b82f6] hover:text-[#2563eb] font-medium transition-colors"
              >
                Edit
              </button>
            </div>
          )}

          {/* Task properties display - matches calendar bar style */}
          {isTaskMode && !editingEntry && (
            <div className="mb-2 px-3 py-2 bg-[#1a1a1a] border border-[#262626] rounded-lg flex items-center justify-between">
              <span className="text-xs text-white">
                {newTaskPriority || newTaskDeadline || newTaskRecurring ? (
                  <span className="flex items-center gap-2 flex-wrap">
                    {newTaskPriority && (
                      <span className="font-medium" style={{ color: getPriorityColor(newTaskPriority) }}>
                        {newTaskPriority.charAt(0).toUpperCase() + newTaskPriority.slice(1)}
                      </span>
                    )}
                    {newTaskDeadline && newTaskPriority && <span className="text-[#737373]">•</span>}
                    {newTaskDeadline && (
                      <span className="font-medium">{format(new Date(newTaskDeadline), 'MMM d, yyyy')}</span>
                    )}
                    {newTaskRecurring && (newTaskPriority || newTaskDeadline) && <span className="text-[#737373]">•</span>}
                    {newTaskRecurring && (
                      <span className="font-medium capitalize">{newTaskRecurring}</span>
                    )}
                  </span>
                ) : (
                  <span className="text-[#737373]">Task properties</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setShowTaskPropertiesEditor(true)}
                className="text-xs text-[#3b82f6] hover:text-[#2563eb] font-medium transition-colors"
              >
                Edit
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="relative flex items-end gap-2 bg-[#1a1a1a] border border-[#262626] rounded-xl p-2">
            {/* Template Menu */}
            <AnimatePresence>
              {showTemplateMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ duration: 0.15 }}
                  className="absolute bottom-full left-0 right-0 mb-2 bg-[#2a2a2a] border-2 border-[#3b82f6] rounded-lg shadow-2xl overflow-hidden z-[100]"
                >
                  {getFilteredTemplates().length > 0 ? (
                    <div className="max-h-[300px] overflow-y-auto">
                      {getFilteredTemplates().map((template, index) => (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => insertTemplate(template)}
                          onMouseEnter={() => setSelectedTemplateIndex(index)}
                          className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                            index === selectedTemplateIndex
                              ? 'bg-[#3b82f6] text-white'
                              : 'text-white hover:bg-[#3a3a3a]'
                          } ${index === 0 ? 'rounded-t-lg' : ''} ${
                            index === getFilteredTemplates().length - 1 ? 'rounded-b-lg' : ''
                          }`}
                        >
                          <span className="text-xl">{template.icon}</span>
                          <span className="text-sm font-medium">{template.name}</span>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="px-4 py-3 text-sm text-[#a3a3a3] text-center">
                      No templates found
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Task properties indicator */}
            {isTaskMode && !editingEntry && (newTaskPriority || newTaskDeadline || newTaskRecurring) && (
              <div className="mb-2 px-3 py-1.5 bg-[#1a1a1a] border border-[#262626] rounded-lg flex flex-wrap items-center gap-2">
                {newTaskPriority && (
                  <span className="text-xs px-2 py-0.5 rounded-md" style={{ backgroundColor: `${getPriorityColor(newTaskPriority)}20`, color: getPriorityColor(newTaskPriority) }}>
                    {newTaskPriority} priority
                  </span>
                )}
                {newTaskDeadline && (
                  <span className="text-xs text-[#a3a3a3]">
                    📅 {format(new Date(newTaskDeadline), 'MMM d')}
                  </span>
                )}
                {newTaskRecurring && (
                  <span className="text-xs text-[#a3a3a3]">
                    🔄 {newTaskRecurring}
                  </span>
                )}
              </div>
            )}

            <textarea
              ref={textareaRef}
              value={newEntry}
              onChange={(e) => {
                const value = e.target.value;
                setNewEntry(value);

                // Stop recording if user starts typing manually
                if (isRecording && recognitionRef.current) {
                  recognitionRef.current.stop();
                  setIsRecording(false);
                }

                // Detect slash command for templates
                if (value.startsWith('/')) {
                  const filter = value.slice(1); // Remove the '/'
                  setTemplateFilter(filter);
                  setShowTemplateMenu(true);
                  setSelectedTemplateIndex(0);
                } else {
                  setShowTemplateMenu(false);
                  setTemplateFilter('');
                }
              }}
              onKeyDown={(e) => {
                // Handle template menu navigation
                if (showTemplateMenu) {
                  const filteredTemplates = getFilteredTemplates();

                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setSelectedTemplateIndex(prev =>
                      prev < filteredTemplates.length - 1 ? prev + 1 : 0
                    );
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setSelectedTemplateIndex(prev =>
                      prev > 0 ? prev - 1 : filteredTemplates.length - 1
                    );
                  } else if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (filteredTemplates.length > 0) {
                      insertTemplate(filteredTemplates[selectedTemplateIndex]);
                    }
                  } else if (e.key === 'Escape') {
                    e.preventDefault();
                    setShowTemplateMenu(false);
                    setTemplateFilter('');
                    setNewEntry('');
                  }
                } else {
                  // Normal key handling

                  // Cmd/Ctrl + Shift + T: Insert task checkbox
                  if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'T') {
                    e.preventDefault();
                    const textarea = textareaRef.current;
                    if (textarea) {
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const text = textarea.value;

                      // Insert "- [ ] " at cursor position
                      const before = text.substring(0, start);
                      const after = text.substring(end);
                      const newText = before + '- [ ] ' + after;

                      setNewEntry(newText);

                      // Set cursor after the inserted text
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start + 6, start + 6);
                      }, 0);
                    }
                  }
                  // Enter: Submit
                  else if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                  // Escape: Cancel/Clear
                  else if (e.key === 'Escape') {
                    if (editingEntry) {
                      handleCancelEdit();
                    } else {
                      setNewEntry('');
                      // Don't turn off task mode if we're in the tasks tab
                      if (activeTab !== 'tasks') {
                        setIsTaskMode(false);
                      }
                      if (textareaRef.current) {
                        textareaRef.current.style.height = '40px';
                      }
                    }
                  }
                }
              }}
              placeholder={editingEntry ? "Edit your entry..." : isRecording ? "Listening..." : "What's on your mind? (/ for templates)"}
              className="flex-1 bg-transparent text-white text-base px-4 py-2 resize-none focus:outline-none placeholder:text-[#737373] leading-[1.5] transition-[height] duration-200 ease-out"
              style={{ minHeight: '40px', maxHeight: '120px', fontSize: '16px' }}
              rows={1}
            />
            
            <div className="flex items-center gap-2 pb-1">
              <motion.button
                type="button"
                onClick={() => {
                  if (activeTab !== 'tasks') {
                    setIsTaskMode(!isTaskMode);
                  }
                }}
                disabled={activeTab === 'tasks'}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  isTaskMode
                    ? 'bg-[#3b82f6] text-white'
                    : 'bg-transparent text-[#a3a3a3] hover:bg-[#262626] hover:text-white'
                } ${activeTab === 'tasks' ? 'opacity-100 cursor-default' : 'cursor-pointer'}`}
                whileTap={activeTab !== 'tasks' ? { scale: 0.95 } : {}}
                title={
                  activeTab === 'tasks'
                    ? "Task mode (always on in Tasks tab)"
                    : isTaskMode
                      ? "Task mode enabled - creates checkable task"
                      : "Click to enable task mode"
                }
              >
                <SafeIcon icon={FiCheckSquare} className="text-lg" />
              </motion.button>

              <motion.button
                type={hasContent ? "submit" : "button"}
                onClick={hasContent ? undefined : toggleDictation}
                disabled={hasContent ? !hasContent : false}
                className={`w-9 h-9 rounded-full flex items-center justify-center transition-all ${
                  hasContent
                    ? 'bg-[#3b82f6] text-white hover:bg-[#2563eb]'
                    : isRecording
                      ? 'bg-red-500 text-white animate-pulse'
                      : 'bg-transparent text-[#a3a3a3] hover:bg-[#262626] hover:text-white'
                }`}
                whileTap={{ scale: 0.95 }}
                title={hasContent ? "Send message" : isRecording ? "Stop recording" : "Start voice dictation"}
              >
                <AnimatePresence mode="wait">
                  {hasContent ? (
                    <motion.div
                      key="send"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0, rotate: 180 }}
                      transition={{ duration: 0.2 }}
                    >
                      <SafeIcon icon={FiSend} className="text-lg" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="mic"
                      initial={{ scale: 0, rotate: 180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      exit={{ scale: 0, rotate: -180 }}
                      transition={{ duration: 0.2 }}
                    >
                      <SafeIcon icon={FiMic} className="text-lg" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default App;